<template>
  <UninstallLayout 
    :title="$t('uninstall.title.sadToSeeYouLeaving')"
    v-bind="isGemPagesV6 ? { isSurveyMediumCol: true } : { isSurveySmallCol: true }"
  >
    <template #titleIcon v-if="isAnsweringSubSurvey">
      <div class="btn-icon" @click="handleBackToMainSurvey">
        <i class="icon icon-arrow-left typo-icon-12"/>
      </div>
    </template>
    <template #leftContent>
      <div class="reasons-list">
        <p class="sub-title mb-2">{{ surveyQuestion }}</p>
        <transition name="slide-fade">
          <div :key="isAnsweringSubSurvey">
            <AnswerItem 
              v-for="answer in surveyAnswers"
              :key="answer.id"
              :answer="answer"
              :isSelected="selectedAnswer && selectedAnswer.id === answer.id"
              @onSelect="handleSelectAnswer(answer)"/>
          </div>
        </transition>
        <transition name="slide-fade">
          <div class="further-question d-flex flex-column" v-if="isShowFurtherSurvey">
            <span class="further-question__label mb-1">{{ furtherSurveyQuestion }}</span>
            <textarea ref="furtherSurveyInput" class="further-question__input" v-model="furtherSurveyAnswer" @input="setFurtherSurvey"/>
          </div>
        </transition>
      </div>
      <div class="d-flex align-items-center">
        <button class="btn btn-secondary2 px-2 mr-2" @click="goToNewSetting" :disabled="disableSecondaryAction">{{ secondaryActionText }}</button>
        <ProcessButton class="btn btn-primary px-2" :status.sync="primaryBtnStatus" :disabled="disablePrimaryAction" @click="handlePrimaryAction" type="submit">
          <template #loading>{{ $t("generals.submit") }}</template>
          <template #success>{{ $t("generals.success") }}</template>
          <template #error>{{ $t("generals.error") }}</template>
          <template #default>{{ primaryActionText }}</template>
        </ProcessButton>
      </div>
    </template>
  </UninstallLayout>
</template>

<script>
import { ProcessButton } from "@/core/shared";
import UninstallLayout from "../components/UninstallLayout.vue";
import AnswerItem from "../components/AnswerItem.vue";
import initGemPagesV7 from "@/mixins/initGemPagesV7.js";
import { mapState, mapActions, mapMutations } from "vuex";
import { REASONS_LEAVING } from "../constants/mainSurvey";
import { SURVEY_TYPE, SURVEY_HANDLE } from "../constants/index";



export default {
  name: 'ReasonToLeave',
  components: {
    UninstallLayout,
    AnswerItem,
    ProcessButton
  },
  mixins: [initGemPagesV7],
  data() {
    return {
      currentSurvey: REASONS_LEAVING,
      surveyType: SURVEY_TYPE.MAIN,
      selectedAnswer: null,
      cacheMainSurveyAnswer: null,
      cacheSubSurveyAnswer: null,
      cacheFurtherSurveyAnswer: null,
      isShowFurtherSurvey: false,
      furtherSurveyAnswer: '',
      primaryBtnStatus: {
        success: false,
        loading: false,
        error: false
      }
    }
  },
  computed: {
    ...mapState("account", {
      instantShopId: "instantShopId",
      shopInfo: "shopInfo"
    }),
    surveyQuestion() {
      return this.$t(this.currentSurvey.question); 
    },
    surveyAnswers() {
      return this.currentSurvey.answers;
    },
    furtherSurveyQuestion() {
      return this.$t(this.currentSurvey.furtherSurveyQuestion); 
    },
    disableSecondaryAction() {
      return this.primaryBtnStatus.loading;
    },
    disablePrimaryAction() {
      return !this.selectedAnswer || (this.selectedAnswer?.isRequired && this.furtherSurveyAnswer?.length === 0);
    },
    primaryActionText() {
      const shouldSubmit = this.isAnsweringSubSurvey || this.isAnsweringMainSurvey && this.selectedAnswer?.isRequired;

      return shouldSubmit 
        ? this.$t("generals.submit") 
        : this.$t("generals.continue");
    },
    secondaryActionText() {
      return this.isAnsweringSubSurvey 
        ? this.$t("uninstall.stayWithUs") 
        : this.$t("uninstall.stayWithGemPages");
    },
    isAnsweringMainSurvey() {
      return this.surveyType === SURVEY_TYPE.MAIN;
    },
    isAnsweringSubSurvey() {
      return this.surveyType === SURVEY_TYPE.SUB;
    },
    isGemPagesV6() {
      return this.shopInfo?.shopType?.isGemPagesV6
    }
  },
  created () {
    if (!this.instantShopId) {
      this.connectGemPagesV7();
    }
  },
  methods: {
    ...mapActions("uninstall", {
      submitSurvey: "submitSurvey",
    }), 
    ...mapMutations("uninstall", {
      setSurveyData: "SET_SURVEY_DATA",
      setFurtherSurveyData: "SET_FURTHER_SURVEY_DATA",
      resetSurveyData: "RESET_SURVEY_DATA",
    }), 
    setFurtherSurvey() {
      const { surveyType, furtherSurveyQuestion, furtherSurveyAnswer } = this;

      const surveyData = {
        type: surveyType,
        data: {
          question: furtherSurveyQuestion,
          answer: furtherSurveyAnswer,
        }
      };

      this.handleCacheFurtherSurveyAnswer();
      this.setFurtherSurveyData(surveyData);
    },
    setSurveyOnSelectAnswer() {
      const { surveyType, surveyQuestion, selectedAnswer } = this;

      const surveyData = {
        type: surveyType,
        data: {
          question: surveyQuestion,
          answer: this.$t(selectedAnswer.label),
        }
      };

      this.setSurveyData(surveyData);
    },
    handleSelectAnswer(answer) {
      this.handleCacheAnswer(answer)
      this.selectedAnswer = answer;
      this.handleShowFurtherSurvey();
      this.setSurveyOnSelectAnswer();
    },
    handleShowFurtherSurvey() {
      const shouldShowFurtherSurvey = this.furtherSurveyQuestion && this.selectedAnswer?.showFurtherSurvey;

      if (shouldShowFurtherSurvey) {
        this.isShowFurtherSurvey = true;
        this.setFocusInput();
      } else {
        this.isShowFurtherSurvey = false;
        this.resetSurveyData({ type: this.surveyType })
      }
    },
    handleCacheAnswer(answer) {
      this.handleCacheMainSurveyAnswer(answer);
      this.handleCacheSubSurveyAnswer(answer);
    },
    handleCacheMainSurveyAnswer(answer) {
      if (this.isAnsweringMainSurvey) {
        const isChangeAnswer =  answer.id  !== this.cacheMainSurveyAnswer?.id;

        if (isChangeAnswer) {
          this.cacheSubSurveyAnswer = null;
          this.cacheFurtherSurveyAnswer = null;
        }

        this.cacheMainSurveyAnswer = answer;
      }
    },
    handleCacheSubSurveyAnswer(answer) {
      if (this.isAnsweringSubSurvey) {
        this.cacheSubSurveyAnswer = answer;
      }
    },
    handleCacheFurtherSurveyAnswer() {
      if (this.isAnsweringSubSurvey) { 
        this.cacheFurtherSurveyAnswer = this.furtherSurveyAnswer;
      }
    },
    handlePrimaryAction() {
      const shouldSubmit = this.isAnsweringSubSurvey || this.isAnsweringMainSurvey && this.selectedAnswer?.isRequired;
      
      shouldSubmit 
        ? this.handleSubmitSurvey()
        : this.handleContinueSurvey()
    },
    handleContinueSurvey() {
      const  { subSurvey } = this.selectedAnswer;

      if (subSurvey) {
        this.currentSurvey = subSurvey;
        this.selectedAnswer = this.cacheSubSurveyAnswer;
        this.furtherSurveyAnswer = this.cacheFurtherSurveyAnswer ,
        this.surveyType = SURVEY_TYPE.SUB;
        this.handleShowFurtherSurvey();
      }
    },
    handleBackToMainSurvey() {
      this.currentSurvey = REASONS_LEAVING;
      this.selectedAnswer = this.cacheMainSurveyAnswer;
      this.furtherSurveyAnswer = this.cacheFurtherSurveyAnswer ,
      this.isShowFurtherSurvey = false;
      this.surveyType = SURVEY_TYPE.MAIN;
    },
    async handleSubmitSurvey() {
      this.primaryBtnStatus.loading = true;
      
      await this.submitSurvey({ surveyHandle: SURVEY_HANDLE });

      this.postSubmitActions();
    },
    postSubmitActions() {
      setTimeout(() => {
        this.primaryBtnStatus.loading = false;
        this.$emit('goToConfirm');
      }, 1500);
    },
    setFocusInput() {
      this.$nextTick(() => {
        if (this.$refs.furtherSurveyInput) {
          this.$refs.furtherSurveyInput.focus();
        }
      });
    },
    goToNewSetting() {
      if (this.disableSecondaryAction) return;
      const domain = this.shopInfo.shopifyDomain.replace('.myshopify.com', '')
      const appId = process.env.VUE_APP_GEMPAGES_APP_ID
      window.top.location.href = `https://admin.shopify.com/store/${domain}/apps/${appId}/app/preferences`
    }
  },
}
</script>

<style scoped lang="scss">
  .btn {
    height: 40px !important;
  }

  .btn-icon {
    margin-right: 12px; 
  }

  .slide-fade-enter-active {
    transition: all 0.5s ease;
  }
  .slide-fade-enter, .slide-fade-leave-to {
    opacity: 0;
    transform: translateX(100px);
    position: absolute;
  }

  .reasons-list {
    width: 564px;
  }
  
  .further-question {
    &__label {
      color: $textLightModeHigh;
      font-size: 14px;
      font-weight: 500;
      line-height: 150%;
    }

    &__input {
      border-radius: 3px;
      min-height: 115px;
      padding: 12px 8px;
      border: solid 1px $neutral7;
      resize: none;

      &:focus {
        border-color: $mono3;
      }
      &:hover:not(:focus) {
        border-color: $dark8;
      }
    }
  }

  .back-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    cursor: pointer;
  }

  .sub-title {
    color: $textLightModeHigh;
    font-size: 18px;
    font-weight: 600;
    line-height: 120%;
  }
</style>
